import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";

// Páginas
import { DashboardPage } from "@/features/Dashboard";
import { ClientApprovalPage } from "@/features/Vacations/pages/ClientApprovalPage";
import { DayOffClientApprovalPage } from "@/features/DpRequests/pages/DayOffClientApprovalPage";

import Proposals from "@/pages/Proposals";
import DepartmentsPage from "@/pages/Departments";
import { ProfileUpdatePage } from "@/features/Collaborators/Edit/Pages";
import { ROLE_COMBINATIONS } from "@/router/proxyRouter.definitions";
import {
  AdminRoutes,
  CollaboratorsRoutes,
  ExpensesRoutes,
  ProjectsRoutes,
  OKRRoutes,
  RoleBasedRoute,
  PeopleRoutes, // Added
  DpPortalRoutes,
  SIRoutes,
  RecruitmentRoutes,
} from "..";
import { CalculatorPage, SavedCalculationsPage, TeamCalculatorPage } from "@/features/Calculator";
import { ROLE_ADMIN, ROLE_CALCULATOR } from "@/contexts/AuthContext";
import { CandidatoFormExternoPage, CandidatoFormExternoEtapa2Page } from "@/features/Recruitment/pages";

function getSafeLoginRedirectTarget(search: string): string {
  const params = new URLSearchParams(search);
  const from = params.get("from");

  if (!from || !from.startsWith("/") || from.startsWith("//") || from.startsWith("/login")) {
    return "/collaborators/directory";
  }

  return from;
}

function AuthenticatedLoginRedirect() {
  const location = useLocation();
  return <Navigate to={getSafeLoginRedirectTarget(location.search)} replace />;
}

export default function PrivateRoutes() {
  return (
    <Routes>
      <Route path="login" element={<AuthenticatedLoginRedirect />} />

      {/* Rotas sem layout específico */}
      <Route path="me/atualizar-cadastro" element={<ProfileUpdatePage />} />
      <Route path="ferias/cliente/:token" element={<ClientApprovalPage />} />
      <Route path="day-off/cliente/:token" element={<DayOffClientApprovalPage />} />

      {/* Mesma rota pública do candidato, também aqui: se quem abrir o link já estiver
          logado no Digital no mesmo navegador (ex.: o próprio recrutador testando o link
          que acabou de gerar), o ProxyRouter escolhe PrivateRoutes em vez de PublicRoutes
          — sem isso aqui, a rota não bate com nada dentro de RecruitmentRoutes (que só tem
          "/") e a página fica em branco. */}
      <Route path="recruitment/candidato/:token" element={<CandidatoFormExternoPage />} />
      <Route path="recruitment/candidato/etapa2/:token" element={<CandidatoFormExternoEtapa2Page />} />

      {/* Layout padrão com rotas principais */}
      <Route path="/" element={<Layout />}>
        {/* Dashboard */}
        <Route
          path="/"
          element={
            <RoleBasedRoute requiredRoles={ROLE_COMBINATIONS.ALL_ROLES}>
              <DashboardPage />
            </RoleBasedRoute>
          }
        />

        {/* Propostas */}
        <Route
          path="proposals"
          element={
            <RoleBasedRoute requiredRoles={ROLE_COMBINATIONS.ADMIN_AND_PROJECT}>
              <Proposals />
            </RoleBasedRoute>
          }
        />

        {/* Departamentos */}
        <Route
          path="departments"
          element={
            <RoleBasedRoute requiredRoles={ROLE_COMBINATIONS.ADMIN_ONLY}>
              <DepartmentsPage />
            </RoleBasedRoute>
          }
        />

        {/* Rotas de Colaboradores */}
        <Route path="collaborators/*" element={<CollaboratorsRoutes />} />

        {/* Departamento pessoal (férias e solicitações ao DP) */}
        <Route
          path="departamento-pessoal/*"
          element={
            <RoleBasedRoute requiredRoles={[]}>
              <DpPortalRoutes />
            </RoleBasedRoute>
          }
        />

        {/* Rotas de Despesas */}
        <Route path="expenses/*" element={<ExpensesRoutes />} />

        {/* Rotas de Projetos */}
        <Route path="projects/*" element={<ProjectsRoutes />} />

        {/* Calculadora */}
        <Route
          path="calculator/team"
          element={
            <RoleBasedRoute requiredRoles={[ROLE_ADMIN, ROLE_CALCULATOR]}>
              <TeamCalculatorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="calculator/saved"
          element={
            <RoleBasedRoute requiredRoles={[ROLE_ADMIN, ROLE_CALCULATOR]}>
              <SavedCalculationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path="calculator"
          element={
            <RoleBasedRoute requiredRoles={[ROLE_ADMIN, ROLE_CALCULATOR]}>
              <CalculatorPage />
            </RoleBasedRoute>
          }
        />

        {/* Rotas Administrativas */}
        <Route path="admin/*" element={<AdminRoutes />} />

        {/* Rotas OKR */}
        <Route
          path="okr/*"
          element={
            <RoleBasedRoute requiredRoles={ROLE_COMBINATIONS.ALL_ROLES}>
              <OKRRoutes />
            </RoleBasedRoute>
          }
        />

        {/* Rotas de Pessoas */}
        <Route path="people/*" element={<PeopleRoutes />} />

        {/* Rotas SI (Suporte de TI interno) */}
        <Route
          path="si/*"
          element={
            <RoleBasedRoute requiredRoles={ROLE_COMBINATIONS.SI_ACCESS}>
              <SIRoutes />
            </RoleBasedRoute>
          }
        />

        {/* Rotas de Recrutamento — qualquer colaborador com alguma role vê o painel;
            as ações (adicionar/editar/excluir candidato, links) ficam restritas a
            recruitment_admin dentro das telas do módulo e em firestore.rules. */}
        <Route
          path="recruitment/*"
          element={
            <RoleBasedRoute requiredRoles={ROLE_COMBINATIONS.ANY_ROLE}>
              <RecruitmentRoutes />
            </RoleBasedRoute>
          }
        />
      </Route>

      {/* Fallback para rotas não encontradas */}
      <Route
        path="*"
        element={<Navigate to="/collaborators/directory" replace />}
      />
    </Routes>
  );
}
